import type { IconKind } from '@/components/AppIcon';

/**
 * Shared shapes for the desktop shell: the toolbar, the row context menu and
 * the detail pane all describe an action the same way, so one definition can
 * feed a toolbar button and a right-click entry without being restated.
 */
export interface DesktopAction {
  label: string;
  onSelect: () => void;
  /**
   * False hides the action outright — used for permissions, so nobody is
   * shown a button they can never press.
   */
  allowed?: boolean;
  /**
   * False shows it greyed with `reason` on hover — used for state ("posted
   * documents cannot be edited"), which teaches the workflow rather than
   * hiding it.
   */
  enabled?: boolean;
  reason?: string;
  primary?: boolean;
  danger?: boolean;
  /** Shown in the context menu, e.g. "F2". Display only; the grid owns keys. */
  shortcut?: string;
  /** Separates groups in the context menu. */
  separatorBefore?: boolean;
  /** Drawn before the label on the toolbar button and in the context menu. */
  icon?: IconKind;
}

export function visibleActions(actions: DesktopAction[]): DesktopAction[] {
  return actions.filter((a) => a.allowed !== false);
}

/**
 * Whose remembered layout this is. Several people share one office PC, so
 * pane sizes, column widths, the chosen farm and the last screen are kept per
 * person: `poultry.split.x` is stored as `poultry.u<id>.split.x`. Set by the
 * auth store before any screen mounts; null (signed out) uses the bare key.
 */
let storageUser: string | null = null;

export function setStorageUser(userId: string | null): void {
  storageUser = userId;
}

/** The key as actually stored for the signed-in person. */
export function scopedKey(key: string): string {
  return storageUser && key.startsWith('poultry.') ? `poultry.u${storageUser}.${key.slice(8)}` : key;
}

/** Reads and writes a small UI preference, surviving a private-mode throw. */
export function readStored<T>(key: string, fallback: T): T {
  try {
    // Falls back to the bare key once, so layout saved before keys became
    // per person is not lost on the first sign-in after the change.
    const raw = localStorage.getItem(scopedKey(key)) ?? localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writeStored(key: string, value: unknown): void {
  try {
    localStorage.setItem(scopedKey(key), JSON.stringify(value));
  } catch {
    // A remembered pane width is never worth breaking the screen over.
  }
}

export function removeStored(key: string): void {
  try {
    localStorage.removeItem(scopedKey(key));
    localStorage.removeItem(key);
  } catch {
    // Nothing to undo.
  }
}

/**
 * Every remembered key of the signed-in person that starts with one of
 * `prefixes` (given unscoped, e.g. 'poultry.split.'), returned unscoped.
 */
export function storedKeys(prefixes: string[]): string[] {
  try {
    const own = storageUser ? `poultry.u${storageUser}.` : null;
    const out = new Set<string>();
    for (let i = 0; i < localStorage.length; i++) {
      const raw = localStorage.key(i);
      if (!raw) continue;
      const key = own && raw.startsWith(own) ? `poultry.${raw.slice(own.length)}` : raw;
      if (/^poultry\.u\d+\./.test(key)) continue; // someone else's
      if (prefixes.some((prefix) => key.startsWith(prefix))) out.add(key);
    }
    return [...out];
  } catch {
    return [];
  }
}
